<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="AdminIndex" label="Admin" />
      <breadcrumb-link view="AdminPayerList" label="Payers" />
      <breadcrumb-link view="AdminPayerUpdate" label="Update" :active="true" />
      <breadcrumb-link
        view="AdminPayerUpdate"
        :params="{ id: payer.id }"
        :label="`Update ${payer.name}`"
        :active="true"
        v-if="!loading"
      />
    </breadcrumb-list>

    <form-base
      title="Update Payer"
      cancel="AdminPayerList"
      :error="form.errors.get('NOFIELD')"
      @submit="handleSubmit"
    >
      <template v-slot:fields>
        <div class="block">
          <form-input :form="form" label="Payer Name" field-name="name" />
        </div>

        <div class="block">
          <form-input
            :form="form"
            :disabled="true"
            label="Payer Internal Name"
            field-name="name_internal"
          />
        </div>

        <div class="block">
          <form-text-area :form="form" label="Note" field-name="note" />
          <p class="help-text">
            Shown to users when they select this payer while creating an inquiry.
          </p>
        </div>

        <!-- A Details Section -->
        <template v-if="payer && payer.code">
          <h3 class="subtitle">A Details</h3>
          <div class="block">
            <form-switch :form="form" label="Is Blue" field-name="is_blue" />
          </div>

          <div class="block">
            <form-switch :form="form" label="Is Group" field-name="is_group" />
          </div>

          <div class="block">
            <form-switch :form="form" label="Is Available" field-name="is_available" />
          </div>

          <div class="block">
            <form-switch :form="form" label="Is Active" field-name="is_active" />
          </div>

          <div class="block">
            <form-switch :form="form" label="Split Service Type" field-name="split_service_type" />
          </div>
        </template>

        <!-- P Details Section -->
        <template v-if="payer && payer.p_code">
          <h3 class="subtitle">P Details</h3>
          <div class="block">
            <form-input :form="form" :disabled="true" label="P Name" field-name="p_name" />
          </div>

          <div class="block">
            <form-switch :form="form" label="P Is Active" field-name="p_is_active" />
          </div>

          <div class="block">
            <form-switch :form="form" label="P Is Available" field-name="p_is_available" />
          </div>
        </template>

        <!-- Source Order Section (for merged payers) -->
        <div class="block" v-if="isMerged">
          <h3 class="subtitle">Source Order</h3>
          <b-field>
            <b-radio v-model="form.source_order" native-value="p_first">P first</b-radio>
            <b-radio v-model="form.source_order" native-value="a_first" class="ml-3">
              A first
            </b-radio>
          </b-field>
        </div>

        <!-- Combo Combinations Section -->
        <h3 class="subtitle">Combo Combinations</h3>
        <div class="block">
          <p class="help-text">
            Drag and drop to reorder the combinations. Selected combinations will be sent to the
            backend in the order shown.
          </p>

          <!-- Selected Combos Section -->
          <div v-if="selectedCombos.length > 0" class="combo-section">
            <h4 class="section-title">Selected Combinations (in order)</h4>
            <div class="combo-list selected-combos">
              <div
                v-for="(combo, index) in selectedCombos"
                :key="combo.id.join(',')"
                class="combo-item selected"
                :class="{ dragging: draggingIndex === index, 'drag-over': dragOverIndex === index }"
                draggable="true"
                @dragstart="handleDragStart(index, $event)"
                @dragover="handleDragOver(index, $event)"
                @drop="handleDrop(index, $event)"
                @dragend="handleDragEnd"
              >
                <div class="drag-handle">⋮⋮</div>
                <b-checkbox
                  v-model="form.combos"
                  :native-value="combo.id"
                  class="combo-checkbox"
                  @change="updateOrderedCombos"
                >
                  <span v-html="combo.name"></span>
                </b-checkbox>
              </div>
            </div>
          </div>

          <!-- Available Combos Section -->
          <div v-if="unselectedCombos.length > 0" class="combo-section">
            <h4 class="section-title">Available Combinations</h4>
            <div class="combo-list available-combos">
              <div
                v-for="(combo, index) in unselectedCombos"
                :key="combo.id.join(',')"
                class="combo-item"
                @dragstart="handleDragStartFromUnselected(combo, $event)"
                @dragover="handleDragOver(index, $event)"
                @drop="handleDrop(index, $event)"
                @dragend="handleDragEnd"
              >
                <div class="drag-handle">⋮⋮</div>
                <b-checkbox
                  v-model="form.combos"
                  :native-value="combo.id"
                  class="combo-checkbox"
                  @change="updateOrderedCombos"
                >
                  <span v-html="combo.name"></span>
                </b-checkbox>
              </div>
            </div>
          </div>
        </div>
      </template>
    </form-base>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Form from '../../utils/form';
import FormBase from '../../components/FormBase.vue';
import FormInput from '../../components/FormInput.vue';
import FormSwitch from '../../components/FormSwitch.vue';
import FormTextArea from '../../components/FormTextArea.vue';
import request from '../../request';

export default {
  name: 'PayerUpdate',
  components: { FormBase, FormInput, FormSwitch, FormTextArea },
  data() {
    return {
      loading: true,
      payer: null,
      draggingIndex: null,
      dragOverIndex: null,
      draggingCombo: null,
      availableCombos: [
        {
          id: ['memberId', 'patientBirthDate', 'patientFirstName', 'patientLastName', 'patientSSN'],
          name: 'Patient ID<br>Date of Birth<br>Patient First Name<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientFirstName', 'patientLastName'],
          name: 'Patient ID<br>Date of Birth<br>Patient First Name<br>Patient Last Name',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientLastName', 'patientSSN'],
          name: 'Patient ID<br>Date of Birth<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientLastName'],
          name: 'Patient ID<br>Date of Birth<br>Patient Last Name',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientFirstName', 'patientSSN'],
          name: 'Patient ID<br>Date of Birth<br>Patient First Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientFirstName'],
          name: 'Patient ID<br>Date of Birth<br>Patient First Name',
        },
        {
          id: ['memberId', 'patientFirstName', 'patientLastName', 'patientSSN'],
          name: 'Patient ID<br>Patient First Name<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientFirstName', 'patientLastName'],
          name: 'Patient ID<br>Patient First Name<br>Patient Last Name',
        },
        {
          id: ['memberId', 'patientBirthDate', 'patientSSN'],
          name: 'Patient ID<br>Date of Birth<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientBirthDate'],
          name: 'Patient ID<br>Date of Birth',
        },
        {
          id: ['memberId', 'patientLastName', 'patientSSN'],
          name: 'Patient ID<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientLastName'],
          name: 'Patient ID<br>Patient Last Name',
        },
        {
          id: ['memberId', 'patientFirstName', 'patientSSN'],
          name: 'Patient ID<br>Patient First Name<br>Patient SSN',
        },
        {
          id: ['memberId', 'patientFirstName'],
          name: 'Patient ID<br>Patient First Name',
        },
        {
          id: ['memberId', 'patientSSN'],
          name: 'Patient ID<br>Patient SSN',
        },
        {
          id: ['memberId'],
          name: 'Patient ID',
        },
        {
          id: ['patientBirthDate', 'patientFirstName', 'patientLastName', 'patientSSN'],
          name: 'Date of Birth<br>Patient First Name<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['patientBirthDate', 'patientFirstName', 'patientLastName'],
          name: 'Date of Birth<br>Patient First Name<br>Patient Last Name',
        },
        {
          id: ['patientBirthDate', 'patientLastName', 'patientSSN'],
          name: 'Date of Birth<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['patientBirthDate', 'patientLastName'],
          name: 'Date of Birth<br>Patient Last Name',
        },
        {
          id: ['patientBirthDate', 'patientFirstName', 'patientSSN'],
          name: 'Date of Birth<br>Patient First Name<br>Patient SSN',
        },
        {
          id: ['patientBirthDate', 'patientFirstName'],
          name: 'Date of Birth<br>Patient First Name',
        },
        {
          id: ['patientBirthDate', 'patientSSN'],
          name: 'Date of Birth<br>Patient SSN',
        },
        {
          id: ['patientBirthDate'],
          name: 'Date of Birth',
        },
        {
          id: ['patientFirstName', 'patientLastName', 'patientSSN'],
          name: 'Patient First Name<br>Patient Last Name<br>Patient SSN',
        },
        {
          id: ['patientFirstName', 'patientLastName'],
          name: 'Patient First Name<br>Patient Last Name',
        },
        {
          id: ['patientFirstName', 'patientSSN'],
          name: 'Patient First Name<br>Patient SSN',
        },
        {
          id: ['patientFirstName'],
          name: 'Patient First Name',
        },
        {
          id: ['patientLastName', 'patientSSN'],
          name: 'Patient Last Name<br>Patient SSN',
        },
        {
          id: ['patientLastName'],
          name: 'Patient Last Name',
        },
        {
          id: ['patientSSN'],
          name: 'Patient SSN',
        },
      ],
      orderedCombos: [], // This will maintain the user's custom order
      form: new Form({
        name: '',
        name_internal: '',
        note: '',
        is_blue: false,
        is_group: false,
        is_available: false,
        is_active: false,
        split_service_type: false,
        p_name: '',
        p_is_active: false,
        p_is_available: false,
        source_order: 'p_first',
        combos: [], // Array to store selected combo IDs
        schema: null, // Will store the schema object
      }),
    };
  },
  computed: {
    isMerged() {
      return !!(this.payer && this.payer.code && this.payer.p_code);
    },
    ...mapGetters(['organization']),
    selectedCombos() {
      // Return combos that are currently selected, in their custom order
      return this.orderedCombos.filter((combo) =>
        this.form.combos.some((selectedCombo) => {
          const selectedComboStr = [...selectedCombo].sort().join(',');
          const comboStr = [...combo.id].sort().join(',');
          return selectedComboStr === comboStr;
        })
      );
    },
    unselectedCombos() {
      // Return combos that are not selected
      return this.orderedCombos.filter(
        (combo) =>
          !this.form.combos.some((selectedCombo) => {
            const selectedComboStr = [...selectedCombo].sort().join(',');
            const comboStr = [...combo.id].sort().join(',');
            return selectedComboStr === comboStr;
          })
      );
    },
  },
  beforeRouteEnter(to, from, next) {
    request
      .get(`/admin/payers/${to.params.id}`)
      .then((resp) => next((vm) => vm.setData(resp.data.payer)))
      .catch(() => next({ name: 'NotFound' }));
  },
  methods: {
    setData(payer) {
      this.payer = payer;

      // Get the schema to check existing combos
      request
        .get(`/admin/payers/${payer.id}`)
        .then((resp) => {
          const schema = resp.data.schema || { combos: [] };
          let selectedCombos = [];

          if (schema?.combos) {
            // For each combo in the schema, find matching available combos
            selectedCombos = this.availableCombos
              .filter((availableCombo) => {
                // Check if this available combo matches any schema combo
                return schema.combos.some((schemaCombo) => {
                  // Compare arrays by converting to strings and checking if they have the same elements
                  const availableComboStr = [...availableCombo.id].sort().join(',');
                  const schemaComboStr = [...schemaCombo].sort().join(',');
                  return availableComboStr === schemaComboStr;
                });
              })
              .map((combo) => [...combo.id]); // Create new arrays for each combo
          }

          // Create a new schema object without any string conversion
          const schemaObject = {
            ...schema,
            combos: schema.combos || [],
          };

          // Initialize orderedCombos with the default order
          this.orderedCombos = [...this.availableCombos];

          this.form = new Form({
            name: payer.name,
            name_internal: payer.name_internal,
            note: payer.note || '',
            is_blue: payer.is_blue,
            is_group: payer.is_group,
            is_available: payer.is_available,
            is_active: payer.is_active,
            split_service_type: payer.split_service_type,
            p_name: payer.p_name,
            p_is_active: payer.p_is_active,
            p_is_available: payer.p_is_available,
            source_order: payer.source_order || 'p_first',
            combos: selectedCombos,
            schema: schemaObject,
          });

          // Update the order to ensure selected combos are in the correct order
          this.updateOrderedCombos();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    handleDragStart(index, event) {
      this.draggingIndex = index;
      this.draggingCombo = this.selectedCombos[index];
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', ''); // Required for Firefox
    },
    handleDragStartFromUnselected(combo, event) {
      this.draggingIndex = null;
      this.draggingCombo = combo;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', ''); // Required for Firefox
    },
    handleDragOver(index, event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.dragOverIndex = index;
    },
    handleDrop(targetIndex, event) {
      event.preventDefault();

      if (this.draggingCombo) {
        // Find the current position of the dragged combo in orderedCombos
        const currentIndex = this.orderedCombos.findIndex(
          (combo) => combo.id.join(',') === this.draggingCombo.id.join(',')
        );

        if (currentIndex !== -1 && currentIndex !== targetIndex) {
          // Reorder the combos
          const newOrderedCombos = [...this.orderedCombos];

          // Remove the dragged item
          newOrderedCombos.splice(currentIndex, 1);

          // Insert at the target position
          newOrderedCombos.splice(targetIndex, 0, this.draggingCombo);

          this.orderedCombos = newOrderedCombos;

          // Update the form combos to maintain the new order
          this.updateOrderedCombos();
        }
      }

      this.draggingIndex = null;
      this.dragOverIndex = null;
      this.draggingCombo = null;
    },
    handleDragEnd() {
      this.draggingIndex = null;
      this.dragOverIndex = null;
      this.draggingCombo = null;
    },
    updateOrderedCombos() {
      // Update form.combos to reflect the current order of selected combos
      // This ensures that only selected combos are sent to the backend,
      // but in the order the user has arranged them
      const selectedCombos = this.orderedCombos
        .filter((combo) =>
          this.form.combos.some((selectedCombo) => {
            const selectedComboStr = [...selectedCombo].sort().join(',');
            const comboStr = [...combo.id].sort().join(',');
            return selectedComboStr === comboStr;
          })
        )
        .map((combo) => [...combo.id]);

      this.form.combos = selectedCombos;
    },
    handleSubmit() {
      this.loading = true;

      // Create a new schema object with the updated combos in the user's order
      const updatedSchema = {
        ...this.form.schema,
        combos: this.form.combos, // Use the combos directly from the form (maintains order)
      };

      // Create a new form instance with only the fields we want to send
      const updateForm = new Form({
        name: this.form.name,
        name_internal: this.form.name_internal,
        note: this.form.note,
        is_blue: this.form.is_blue,
        is_group: this.form.is_group,
        is_available: this.form.is_available,
        is_active: this.form.is_active,
        split_service_type: this.form.split_service_type,
        p_name: this.form.p_name,
        p_is_active: this.form.p_is_active,
        p_is_available: this.form.p_is_available,
        source_order: this.form.source_order,
        schema: updatedSchema, // Send schema as an object
      });

      updateForm
        .put(`/admin/payers/${this.$route.params.id}`)
        .then(() => {
          let msg = `${this.form.name} was updated successfully.`;
          this.$buefy.toast.open({ message: msg, type: 'is-success' });
          this.$router.push({ name: 'AdminPayerDetail', params: { id: this.payer.id } });
        })
        .catch(() => {})
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.subtitle {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.help-text {
  margin-bottom: 1rem;
  color: #666;
  font-size: 0.9rem;
}

.combo-section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
}

.selected-combos {
  border: 2px solid #3273dc;
  background-color: #f0f8ff;
}

.available-combos {
  border: 1px solid #ddd;
  background-color: #f8f9fa;
}

.combo-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.combo-item {
  padding: 0.5rem;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #eee;
  min-height: 60px;
  display: flex;
  align-items: center;
  cursor: move;
  transition: all 0.2s ease;
  position: relative;
}

.combo-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.combo-item.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.combo-item.drag-over {
  border-color: #3273dc;
  background-color: #f0f8ff;
}

.drag-handle {
  color: #999;
  font-size: 1.2rem;
  margin-right: 0.5rem;
  cursor: move;
  user-select: none;
}

.combo-checkbox {
  flex: 1;
}

.combo-checkbox :deep(.checkbox) {
  width: 100%;
  padding: 0.5rem;
  white-space: pre-line;
}

.combo-checkbox :deep(.checkbox:hover) {
  background-color: #f5f5f5;
}
</style>
